import React, { useState, useEffect } from "react";
import { Plus, PiggyBank, Trash2, AlertTriangle } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { budgets as api, type Budget, type BudgetVariance, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const CATEGORIES = ["Maintenance", "Utilities", "Security", "Housekeeping", "Repairs", "Landscaping", "Admin", "Other"];

const fmtAmount = (n: number | undefined) =>
  `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const currentMonth = () => new Date().toISOString().slice(0, 7);

const INIT = { category: "Maintenance", period: currentMonth(), budgetedAmount: "" };

const BudgetsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(currentMonth());
  const [variance, setVariance] = useState<BudgetVariance | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmBudget, setConfirmBudget] = useState<Budget | null>(null);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  useEffect(() => {
    if (!societyId) return;
    api.getVariance(societyId, period).then(setVariance).catch(() => setVariance(null));
  }, [societyId, period, items]);

  const openAdd = () => { setFormError(""); setForm({ ...INIT, period }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setForm(INIT); setFormError(""); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError("");
    try {
      const created = await api.create(societyId, {
        category: form.category, period: form.period, budgetedAmount: Number(form.budgetedAmount),
      });
      setItems(p => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to save budget.");
    } finally { setSaving(false); }
  };

  const handleDelete = (b: Budget) => { setConfirmBudget(b); setDeleteError(""); };

  const confirmDelete = async () => {
    if (!societyId || !confirmBudget) return;
    setDeletingId(confirmBudget.id);
    try {
      await api.delete(societyId, confirmBudget.id);
      setItems(p => p.filter(x => x.id !== confirmBudget.id));
      setConfirmBudget(null);
    } catch { setDeleteError("Failed to delete budget."); }
    finally { setDeletingId(null); }
  };

  const periodBudgets = items.filter(b => b.period === period);

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Budgets
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>Budget vs. actual spend by category</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={{ ...inp, width: 160 }} />
          <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
            <Plus size={14} /> Add Budget
          </button>
        </div>
      </div>

      {variance && variance.categories.some(c => c.overBudget) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, fontSize: 12, color: "#DC2626", marginBottom: 16 }}>
          <AlertTriangle size={14} />
          {variance.categories.filter(c => c.overBudget).length} categor{variance.categories.filter(c => c.overBudget).length !== 1 ? "ies" : "y"} over budget this period.
        </div>
      )}

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            { key: "category", header: "Category", cellStyle: { fontWeight: 500 }, render: (b: Budget) => b.category },
            { key: "budgeted", header: "Budgeted", render: (b: Budget) => fmtAmount(b.budgetedAmount) },
            {
              key: "actual", header: "Actual",
              render: (b: Budget) => {
                const cv = variance?.categories.find(c => c.category === b.category);
                return cv ? fmtAmount(cv.actualAmount) : "—";
              },
            },
            {
              key: "variance", header: "Variance",
              render: (b: Budget) => {
                const cv = variance?.categories.find(c => c.category === b.category);
                if (!cv) return "—";
                return (
                  <span style={{ color: cv.overBudget ? "#DC2626" : "#10B981", fontWeight: 600 }}>
                    {cv.overBudget ? "−" : "+"}{fmtAmount(Math.abs(cv.variance))}
                  </span>
                );
              },
            },
            {
              key: "status", header: "Status",
              render: (b: Budget) => {
                const cv = variance?.categories.find(c => c.category === b.category);
                if (!cv) return null;
                return (
                  <span style={{ padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 500, background: cv.overBudget ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)", color: cv.overBudget ? "#DC2626" : "#10B981" }}>
                    {cv.overBudget ? "Over Budget" : "On Track"}
                  </span>
                );
              },
            },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (b: Budget) => (
                <button onClick={() => handleDelete(b)} disabled={deletingId === b.id} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6, color: "#EF4444", opacity: deletingId === b.id ? 0.4 : 1, display: "inline-flex", alignItems: "center" }} title="Delete budget">
                  <Trash2 size={14} />
                </button>
              ),
            },
          ]}
          rows={periodBudgets}
          rowKey={b => b.id}
          loading={loading}
          emptyIcon={<PiggyBank size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText={`No budgets set for ${period}.`}
        />
      </div>

      <ConfirmDialog
        open={confirmBudget !== null}
        variant="danger"
        title={`Delete budget for "${confirmBudget?.category}"?`}
        message="This budget will be permanently removed."
        confirmLabel="Delete Budget"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setConfirmBudget(null); setDeleteError(""); }}
      />

      {showForm && (
        <Modal
          title="Add Budget"
          submitLabel="Add Budget"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Category *</label>
            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={inp}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Period *</label>
            <input type="month" value={form.period} onChange={e => setForm(p => ({ ...p, period: e.target.value }))} required style={inp} />
          </div>
          <div>
            <label style={lbl}>Budgeted Amount (₹) *</label>
            <input type="number" value={form.budgetedAmount} onChange={e => setForm(p => ({ ...p, budgetedAmount: e.target.value }))} required min="0" step="0.01" placeholder="10000" style={inp} />
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default BudgetsPage;
